import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useRef } from "react";

const FEATURES = [
  { title: "Fresh Daily", desc: "Batter prepared every single morning." },
  { title: "100% Pure Veg", desc: "Strict vegetarian kitchen, zero compromise." },
  { title: "Homemade Taste", desc: "Recipes passed down through generations." },
  { title: "Fast Delivery", desc: "Hot, packed and at your door — quickly." },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-[#FFF8F1] overflow-hidden"
    >
      <div className="absolute -left-32 top-20 w-96 h-96 rounded-full bg-[#2E7D32]/8 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center relative">
        {/* Image with parallax */}
        <motion.div
          style={{ y }}
          className="relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)] border-[10px] border-white">
            <img
              src="https://images.pexels.com/photos/3531700/pexels-photo-3531700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Authentic South Indian cooking"
              className="w-full h-[500px] object-cover"
              data-testid="about-image"
            />
          </div>
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
            className="absolute -bottom-6 -right-6 md:-right-10 bg-white rounded-2xl shadow-2xl px-6 py-5 border border-orange-100 max-w-[240px]"
          >
            <div className="font-serif text-4xl font-extrabold text-[#F58220]">24h</div>
            <div className="text-sm font-medium text-[#1E1E1E] mt-1">Fermented Batter</div>
            <div className="text-xs text-[#555555] mt-1">For that signature tang</div>
          </motion.div>
        </motion.div>

        {/* Text content */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-block text-xs uppercase tracking-[0.3em] text-[#F58220] font-semibold mb-3">
            Why Choose Us
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#1E1E1E] leading-tight">
            Why Choose{" "}
            <span className="text-[#F58220]">Zingly Dosa</span>?
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#555555] leading-relaxed">
            Fresh batter made every day. Homemade taste. No compromise on quality.
            Prepared hygienically in small batches and served piping hot — straight from our
            kitchen in Beed to your plate.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-orange-50 shadow-[0_8px_24px_-12px_rgba(245,130,32,0.2)]"
                data-testid={`feature-${i}`}
              >
                <CheckCircle2 className="w-6 h-6 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#1E1E1E]">{f.title}</div>
                  <div className="text-sm text-[#555555] mt-0.5">{f.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
